import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import flat from "../assets/properties/flat1.png";
import land from "../assets/properties/land.png";
import shop from "../assets/properties/shop.png";
import { encodeId } from "../utils/idEncoder";

export default function PropertyCard({ p }) {
  const title = p.title || p.TagLine || p.property_title || "Property Listing";
  const propertyTypeRaw = String(p.type || p.PropertyType || p.property_type || "house").toLowerCase();
  const propertyType = propertyTypeRaw === "flat" ? "house" : propertyTypeRaw;

  const city = p.city || p.City || "-";
  const location = p.location_label || p.Location || p.locality || "-";
  const status = p.status || "published";
  const price = p.price ?? p.Price ?? 0;
  const bedrooms = p.bedrooms ?? p.NumberOfRooms ?? p.bedrooms ?? 0;
  const bathrooms = p.bathrooms ?? p.NumberOfWashrooms ?? p.washrooms ?? 0;
  const areaSqft = p.area_sqft ?? p.Area ?? null;
  const shopUnits = p.shop_units ?? p.NumberOfShops ?? 0;
  const images = useMemo(
    () => getImageList(p.property_images || p.propertyImages, propertyType),
    [p.property_images, p.propertyImages, propertyType]
  );
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const onPrevImage = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const onNextImage = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveImageIndex((prev) => (prev + 1) % images.length);
  };

  const onSelectImage = (event, index) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveImageIndex(index);
  };

  return (
    <Link to={`/property/${encodeId(p.id)}`} className="card">
      <div className="card-media-wrap">
        <img src={images[activeImageIndex]} alt={title} />
        {images.length > 1 ? (
          <>
            <button type="button" className="card-image-nav prev" onClick={onPrevImage} aria-label="Previous image">
              ‹
            </button>
            <button type="button" className="card-image-nav next" onClick={onNextImage} aria-label="Next image">
              ›
            </button>
            <div className="card-image-dots">
              {images.map((item, index) => (
                <button
                  key={`${item.slice(0, 24)}-${index}`}
                  type="button"
                  className={`card-image-dot ${index === activeImageIndex ? "active" : ""}`}
                  aria-label={`Show image ${index + 1}`}
                  onClick={(event) => onSelectImage(event, index)}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>
      <div className="card-content">
        <div className="card-badge">{status}</div>
        <h3>{title}</h3>
        <p className="card-meta-text">
          {location} • {city} • {propertyType}
        </p>

        {propertyType === "house" ? (
          <p className="card-info-text">
            {bedrooms} bedrooms • {bathrooms} bathrooms
          </p>
        ) : null}

        {propertyType === "land" && areaSqft ? (
          <p className="card-info-text">Area: {areaSqft} sqft</p>
        ) : null}

        {propertyType === "shop" ? (
          <p className="card-info-text">
            Units: {shopUnits} • Bathrooms: {bathrooms}
          </p>
        ) : null}

        <div className="price">₹ {price}</div>
      </div>
    </Link>
  );
}

export const getImageList = (img, propertyType = "") => {
  if (Array.isArray(img)) {
    const normalized = img.map((item) => getImages(item, propertyType)).filter(Boolean);
    return normalized.length ? normalized : [getImages(null, propertyType)];
  }
  return [getImages(img, propertyType)];
};

export const getImages = (img, propertyType = "") => {
  const normalizedType = String(propertyType || "").toLowerCase();
  const fallback = () => {
    if (normalizedType === "shop") return shop;
    if (normalizedType === "land") return land;
    return flat;
  };

  if (Array.isArray(img) && img.length) {
    return getImages(img[0], propertyType);
  }

  if (!img) {
    return fallback();
  }

  const raw = String(img).trim();
  if (
    raw.startsWith("http://") ||
    raw.startsWith("https://") ||
    raw.startsWith("data:image/") ||
    raw.startsWith("blob:")
  ) {
    return raw;
  }

  switch (raw) {
    case "shop":
      return shop;
    case "land":
      return land;
    case "flat":
      return flat;
    default:
      return fallback();
  }
};
